import type { Chat } from "@ai-sdk/react";
import { createContext, type ReactNode, useContext, useEffect, useState } from "react";
import { addReloadHoldCheck } from "../shell/build-refresh.ts";
import type { ChatMessage } from "./MessageView.tsx";

/**
 * Chats kept across route changes (design §7): leaving a conversation through the sidebar while
 * its reply streams keeps it streaming, and coming back shows it. A settled chat is dropped on
 * leaving, so a later visit loads the history again under current access (hidden messages,
 * others' turns). A chat started elsewhere (home, "New conversation") is handed over through it.
 */
export interface ChatCache {
  /** The kept chat of `id`, if still worth reusing (streaming, or handed over and not opened). */
  take(id: string): Chat<ChatMessage> | undefined;
  /** Keeps a chat; `handedOver` marks one started before its thread opened. */
  keep(id: string, chat: Chat<ChatMessage>, handedOver?: boolean): void;
  /** Leaving the thread: kept only while its reply streams. */
  leave(id: string, chat: Chat<ChatMessage>): void;
  /** A kept chat's reply is still streaming (it holds a new build's reload). */
  streaming(): boolean;
}

const busy = (chat: Chat<ChatMessage>) =>
  chat.status === "submitted" || chat.status === "streaming";

export function createChatCache(): ChatCache {
  const chats = new Map<string, { chat: Chat<ChatMessage>; handedOver: boolean }>();
  return {
    take(id) {
      const kept = chats.get(id);
      if (!kept) {
        return undefined;
      }
      if (kept.handedOver || busy(kept.chat)) {
        return kept.chat;
      }
      chats.delete(id);
      return undefined;
    },
    keep(id, chat, handedOver = false) {
      chats.set(id, { chat, handedOver });
    },
    leave(id, chat) {
      if (busy(chat)) {
        chats.set(id, { chat, handedOver: false });
      } else if (chats.get(id)?.chat === chat) {
        chats.delete(id);
      }
    },
    streaming() {
      return [...chats.values()].some((kept) => busy(kept.chat));
    },
  };
}

const ChatCacheContext = createContext<ChatCache | null>(null);

export function ChatCacheProvider({ children }: { children: ReactNode }) {
  const [cache] = useState(createChatCache);
  useEffect(() => addReloadHoldCheck(() => cache.streaming()), [cache]);
  return <ChatCacheContext.Provider value={cache}>{children}</ChatCacheContext.Provider>;
}

/** The app's chat cache (a private one outside the provider, e.g. in isolated tests). */
export function useChatCache(): ChatCache {
  const shared = useContext(ChatCacheContext);
  const [own] = useState(createChatCache);
  return shared ?? own;
}
